import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, School, ClipboardList, CalendarDays, Wallet, GraduationCap,
  Plus, Trash2, KeyRound, Save, Shield, Edit3, Copy, Check, History, User as UserIcon, RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ROLES = ["principal", "teacher", "student", "parent", "accountant", "hr", "admin"];
const GRADES = ["R", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const TERMS = [1, 2, 3, 4];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const PERIODS = [1, 2, 3, 4, 5, 6];
const zar = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

const errText = (e) => formatApiError(e.response?.data?.detail) || e.message;

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------
function OverviewTab({ setTab }) {
  const [summary, setSummary] = useState(null);
  useEffect(() => { api.get("/admin/summary").then((r) => setSummary(r.data)); }, []);
  if (!summary) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const kpis = [
    { label: "Learners", value: summary.users_by_role.student, hint: `${summary.classes} classes` },
    { label: "Teachers", value: summary.users_by_role.teacher, hint: `${summary.users_by_role.principal + summary.users_by_role.admin} admins` },
    { label: "Marks entered", value: summary.marks, hint: `${summary.exams} exams` },
    { label: "Invoices paid", value: `${summary.invoices_paid}/${summary.invoices}`, hint: `${summary.homework} HW · ${summary.submissions} submissions` },
  ];

  const shortcuts = [
    { key: "people", label: "Add / edit users", icon: Users, tab: "people" },
    { key: "classes", label: "Set up classes", icon: School, tab: "classes" },
    { key: "marks", label: "Enter exam marks", icon: ClipboardList, tab: "marks" },
    { key: "timetable", label: "Edit timetable", icon: CalendarDays, tab: "timetable" },
    { key: "fees", label: "Batch invoice fees", icon: Wallet, tab: "fees" },
    { key: "submissions", label: "Grade submissions", icon: GraduationCap, tab: "submissions" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid gap-4 md:grid-cols-4">
        {kpis.map((k) => (
          <Card key={k.label} className="rounded-2xl">
            <CardContent className="p-6">
              <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">{k.label}</div>
              <div className="mt-2 text-3xl font-bold tracking-tight">{k.value}</div>
              <div className="mt-1 text-xs text-muted-foreground">{k.hint}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="text-base">People by role</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {ROLES.map((r) => (
            <div key={r} className="p-4 rounded-xl bg-muted/40">
              <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">{r}</div>
              <div className="text-2xl font-bold mt-1">{summary.users_by_role[r] || 0}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="text-base">Quick actions</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3">
          {shortcuts.map((s) => (
            <button key={s.key} onClick={() => setTab(s.tab)}
              className="lift p-4 rounded-xl bg-muted/40 flex items-center gap-3 text-left"
              data-testid={`admin-shortcut-${s.key}`}>
              <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center">
                <s.icon className="h-5 w-5" />
              </div>
              <div className="text-sm font-medium">{s.label}</div>
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// People tab
// ---------------------------------------------------------------------------
const EMPTY_USER = { email: "", name: "", role: "student", class_id: "", grade: "", parent_email: "", phone: "", password: "" };

function PeopleTab({ classes }) {
  const [users, setUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState("all");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);
  const [tempPw, setTempPw] = useState(null);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const classMap = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c.name])), [classes]);

  const filtered = users.filter((u) =>
    (roleFilter === "all" || u.role === roleFilter) &&
    ((u.name || "").toLowerCase().includes(q.toLowerCase()) ||
     (u.email || "").toLowerCase().includes(q.toLowerCase()))
  );

  const openCreate = () => { setForm(EMPTY_USER); setTempPw(null); setCreating(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email, name: u.name || "", role: u.role,
      class_id: u.class_id || "", grade: u.grade || "",
      parent_email: u.parent_email || "", phone: u.phone || "", password: "",
    });
  };

  const submitCreate = async () => {
    try {
      const { data } = await api.post("/admin/users", {
        email: form.email, name: form.name, role: form.role,
        password: form.password || null,
        class_id: form.class_id || null,
        grade: form.grade || null,
        parent_email: form.parent_email || null,
        phone: form.phone || null,
      });
      if (data.temp_password) {
        setTempPw(data.temp_password);
        toast.success("User created — auto-generated password below");
      } else {
        toast.success("User created");
        setCreating(false);
      }
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const submitEdit = async () => {
    try {
      await api.patch(`/admin/users/${editing.id}`, {
        name: form.name, role: form.role,
        class_id: form.class_id || null, grade: form.grade || null,
        parent_email: form.parent_email || null, phone: form.phone || null,
      });
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const resetPw = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      toast.success(`Password reset for ${u.name}`, { description: `Temp password: ${data.temp_password}` });
    } catch (e) { toast.error(errText(e)); }
  };

  const doDelete = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Removed ${u.name}`);
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const showStudentFields = form.role === "student";

  const formBody = (isCreate) => (
    <div className="space-y-3 py-2">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
          <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                 disabled={!isCreate} className="rounded-xl mt-1.5" data-testid="admin-user-email" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Full name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5" data-testid="admin-user-name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className="rounded-xl mt-1.5" data-testid="admin-user-role"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Phone</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl mt-1.5" />
        </div>
        {showStudentFields && (
          <>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Grade</Label>
              <Select value={form.grade || "__none"} onValueChange={(v) => setForm({ ...form, grade: v === "__none" ? "" : v })}>
                <SelectTrigger className="rounded-xl mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— none —</SelectItem>
                  {GRADES.map((g) => <SelectItem key={g} value={g}>Grade {g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Class</Label>
              <Select value={form.class_id || "__none"} onValueChange={(v) => setForm({ ...form, class_id: v === "__none" ? "" : v })}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="admin-user-class"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— unassigned —</SelectItem>
                  {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Parent email (for parent portal linkage)</Label>
              <Input value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className="rounded-xl mt-1.5" data-testid="admin-user-parent-email" />
            </div>
          </>
        )}
        {isCreate && (
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Password (leave blank to auto-generate)</Label>
            <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl mt-1.5" placeholder="Auto" />
          </div>
        )}
      </div>
      {tempPw && (
        <div className="p-3 rounded-xl bg-primary/10 border border-primary/25 text-sm">
          <div className="text-[0.65rem] tracking-widest uppercase text-primary mb-1">Auto-generated password</div>
          <div className="font-mono flex items-center justify-between gap-2">
            <span data-testid="admin-user-temp-password">{tempPw}</span>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(tempPw); toast.success("Copied"); }}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="text-xs text-muted-foreground mt-1">Copy this and share it via a safe channel — it will not be shown again.</div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-people">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Filter role</Label>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="rounded-xl w-40 mt-1.5" data-testid="admin-people-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[240px]">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Search</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email…" className="rounded-xl mt-1.5" />
        </div>
        <Button onClick={openCreate} className="rounded-xl gap-1 h-11" data-testid="admin-user-new">
          <Plus className="h-4 w-4" /> Add user
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="p-0">
          <div className="divide-y divide-border/60">
            <div className="grid grid-cols-[1.7fr,1fr,1fr,1fr,auto] gap-3 px-4 py-3 text-[0.65rem] tracking-widest uppercase text-muted-foreground">
              <div>Name / email</div><div>Role</div><div>Class</div><div>Grade</div><div>Actions</div>
            </div>
            {filtered.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No users match.</div>}
            {filtered.map((u) => (
              <div key={u.id} className="grid grid-cols-[1.7fr,1fr,1fr,1fr,auto] gap-3 px-4 py-3 items-center" data-testid={`admin-user-${u.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
                <Badge variant="outline" className="rounded-full capitalize w-fit">{u.role}</Badge>
                <div className="text-sm truncate">{classMap[u.class_id] || "—"}</div>
                <div className="text-sm">{u.grade ? `Grade ${u.grade}` : "—"}</div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`admin-edit-${u.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => resetPw(u)} data-testid={`admin-reset-${u.id}`}><KeyRound className="h-3.5 w-3.5" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="ghost" className="text-red-500" data-testid={`admin-delete-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-2xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove {u.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This permanently deletes the account. Their data (attendance, fees, marks) remains but becomes unlinked.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => doDelete(u)} className="rounded-xl bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={creating} onOpenChange={(v) => { setCreating(v); if (!v) setTempPw(null); }}>
        <DialogContent className="rounded-2xl max-w-2xl">
          <DialogHeader>
            <DialogTitle>New user</DialogTitle>
            <DialogDescription>Add a person directly. For student accounts, link their class + parent email now to avoid gaps.</DialogDescription>
          </DialogHeader>
          {formBody(true)}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setTempPw(null); }} className="rounded-xl">Close</Button>
            {!tempPw && <Button onClick={submitCreate} className="rounded-xl" data-testid="admin-user-create-submit">Create user</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="rounded-2xl max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>Change any field. Email cannot be changed.</DialogDescription>
          </DialogHeader>
          {formBody(false)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={submitEdit} className="rounded-xl" data-testid="admin-user-edit-submit">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Classes tab
// ---------------------------------------------------------------------------
function ClassesTab({ classes, refresh, teachers, studentsByClass }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", grade: "8", teacher_id: "", room: "" });
  const [editing, setEditing] = useState(null);

  const submitCreate = async () => {
    try {
      await api.post("/classes", {
        name: form.name, grade: form.grade,
        teacher_id: form.teacher_id || null, room: form.room || null,
      });
      toast.success("Class created");
      setCreating(false);
      setForm({ name: "", grade: "8", teacher_id: "", room: "" });
      refresh();
    } catch (e) { toast.error(errText(e)); }
  };

  const submitEdit = async () => {
    try {
      await api.patch(`/classes/${editing.id}`, {
        name: form.name, grade: form.grade,
        teacher_id: form.teacher_id || null, room: form.room || null,
      });
      toast.success("Class updated");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(errText(e)); }
  };

  const doDelete = async (c) => {
    try {
      await api.delete(`/classes/${c.id}`);
      toast.success("Class removed");
      refresh();
    } catch (e) { toast.error(errText(e)); }
  };

  const teacherMap = Object.fromEntries(teachers.map((t) => [t.id, t.name]));
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, grade: c.grade, teacher_id: c.teacher_id || "", room: c.room || "" });
  };

  const classForm = (
    <div className="grid grid-cols-2 gap-3 py-2">
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5" placeholder="Grade 8A" data-testid="admin-class-name" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Grade</Label>
        <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
          <SelectTrigger className="rounded-xl mt-1.5"><SelectValue /></SelectTrigger>
          <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g}>Grade {g}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Class teacher</Label>
        <Select value={form.teacher_id || "__none"} onValueChange={(v) => setForm({ ...form, teacher_id: v === "__none" ? "" : v })}>
          <SelectTrigger className="rounded-xl mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">— unassigned —</SelectItem>
            {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Room</Label>
        <Input value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} className="rounded-xl mt-1.5" />
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-classes">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} className="rounded-xl gap-1" data-testid="admin-class-new"><Plus className="h-4 w-4" /> New class</Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {classes.length === 0 && <div className="col-span-full text-sm text-muted-foreground">No classes yet.</div>}
        {classes.map((c) => (
          <Card key={c.id} className="rounded-2xl lift" data-testid={`admin-class-${c.id}`}>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground">Grade {c.grade}</div>
                <CardTitle className="text-lg mt-1">{c.name}</CardTitle>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(c)}><Edit3 className="h-3.5 w-3.5" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="text-red-500"><Trash2 className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
                  <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {c.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Removes the class and its timetable. Learners still linked to this class must be reassigned first.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => doDelete(c)} className="rounded-xl bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex justify-between"><span className="text-muted-foreground">Teacher</span><span>{teacherMap[c.teacher_id] || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Room</span><span>{c.room || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Learners</span><Badge className="rounded-full">{studentsByClass[c.id] || 0}</Badge></div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={creating || !!editing} onOpenChange={(v) => { if (!v) { setCreating(false); setEditing(null); } }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.name}` : "New class"}</DialogTitle></DialogHeader>
          {classForm}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }} className="rounded-xl">Cancel</Button>
            <Button onClick={editing ? submitEdit : submitCreate} className="rounded-xl" data-testid="admin-class-submit">
              {editing ? "Save changes" : "Create class"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Marks Entry tab
// ---------------------------------------------------------------------------
function MarksTab({ classes }) {
  const [classId, setClassId] = useState("");
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState("");
  const [students, setStudents] = useState([]);
  const [entries, setEntries] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!classId) { setExams([]); return; }
    api.get("/exams", { params: { class_id: classId } }).then((r) => {
      setExams(r.data);
      if (r.data[0]) setExamId(r.data[0].id);
      else setExamId("");
    });
    api.get("/users", { params: { role: "student", class_id: classId } }).then((r) => setStudents(r.data));
  }, [classId]);

  useEffect(() => {
    if (!examId) { setEntries({}); return; }
    api.get("/exams/marks", { params: { exam_id: examId } }).then((r) => {
      const m = {};
      r.data.forEach((x) => { m[x.student_id] = { marks: x.marks, comment: x.comment || "" }; });
      setEntries(m);
    });
  }, [examId]);

  const exam = exams.find((e) => e.id === examId);

  const setEntry = (sid, patch) => setEntries((prev) => ({ ...prev, [sid]: { ...(prev[sid] || {}), ...patch } }));

  const save = async () => {
    if (!examId) return;
    setSaving(true);
    try {
      const list = Object.entries(entries)
        .filter(([, v]) => v && v.marks !== "" && v.marks != null)
        .map(([student_id, v]) => ({ student_id, marks: Number(v.marks), comment: v.comment || "" }));
      const { data } = await api.post("/admin/marks/bulk", { exam_id: examId, entries: list });
      toast.success(`Saved ${data.count} mark(s)`);
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-marks">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Class</Label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="rounded-xl mt-1.5 w-48" data-testid="admin-marks-class"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Exam</Label>
          <Select value={examId} onValueChange={setExamId} disabled={!classId || exams.length === 0}>
            <SelectTrigger className="rounded-xl mt-1.5 w-64" data-testid="admin-marks-exam"><SelectValue placeholder={classId ? (exams.length ? "Select exam" : "No exams") : "Pick class first"} /></SelectTrigger>
            <SelectContent>{exams.map((e) => <SelectItem key={e.id} value={e.id}>{e.subject} · T{e.term} · {e.date}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {exam && (
          <Badge variant="outline" className="rounded-full h-8 self-end">Out of {exam.total_marks}</Badge>
        )}
        <div className="flex-1" />
        <Button onClick={save} disabled={!examId || saving} className="rounded-xl gap-1 h-11" data-testid="admin-marks-save">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save all marks"}
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="p-0">
          <div className="divide-y divide-border/60">
            <div className="grid grid-cols-[2fr,120px,2fr] gap-3 px-4 py-3 text-[0.65rem] tracking-widest uppercase text-muted-foreground">
              <div>Learner</div><div>Marks</div><div>Comment</div>
            </div>
            {!classId && <div className="p-8 text-center text-sm text-muted-foreground">Select a class to begin.</div>}
            {classId && students.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No learners in this class.</div>}
            {classId && students.map((s) => (
              <div key={s.id} className="grid grid-cols-[2fr,120px,2fr] gap-3 px-4 py-3 items-center" data-testid={`admin-mark-row-${s.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground truncate">Grade {s.grade || "—"}</div>
                </div>
                <Input
                  type="number" min="0" max={exam?.total_marks || 100}
                  value={entries[s.id]?.marks ?? ""}
                  onChange={(e) => setEntry(s.id, { marks: e.target.value })}
                  disabled={!examId}
                  placeholder="—"
                  className="rounded-xl h-9"
                  data-testid={`admin-mark-input-${s.id}`}
                />
                <Input
                  value={entries[s.id]?.comment ?? ""}
                  onChange={(e) => setEntry(s.id, { comment: e.target.value })}
                  disabled={!examId}
                  placeholder="Optional comment"
                  className="rounded-xl h-9"
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timetable Editor tab
// ---------------------------------------------------------------------------
function TimetableTab({ classes, teachers }) {
  const [classId, setClassId] = useState("");
  const [slots, setSlots] = useState([]);
  const [editing, setEditing] = useState(null);
  const [subject, setSubject] = useState("");
  const [teacherId, setTeacherId] = useState("");

  const load = async () => {
    if (!classId) { setSlots([]); return; }
    const { data } = await api.get("/timetable", { params: { class_id: classId } });
    setSlots(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [classId]);

  const grid = {};
  slots.forEach((s) => { grid[`${s.day}-${s.period}`] = s; });

  const openSlot = (day, period) => {
    const s = grid[`${day}-${period}`];
    setEditing({ day, period });
    setSubject(s?.subject || "");
    setTeacherId(s?.teacher_id || "");
  };

  const saveSlot = async () => {
    try {
      await api.post("/timetable", {
        class_id: classId, day: editing.day, period: editing.period,
        subject, teacher_id: teacherId || null,
      });
      toast.success(`${editing.day} P${editing.period} saved`);
      setEditing(null);
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const clearSlot = async () => {
    try {
      await api.post("/timetable/delete", { class_id: classId, day: editing.day, period: editing.period });
      toast.success("Slot cleared");
      setEditing(null);
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-timetable">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Class</Label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="rounded-xl mt-1.5 w-48" data-testid="admin-tt-class"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {!classId ? (
        <Card className="rounded-2xl border-dashed"><CardContent className="p-8 text-center text-sm text-muted-foreground">Select a class to edit its timetable.</CardContent></Card>
      ) : (
        <Card className="rounded-2xl">
          <CardContent className="p-6">
            <div className="grid grid-cols-6 gap-2 text-sm">
              <div />
              {DAYS.map((d) => <div key={d} className="text-[0.65rem] tracking-widest uppercase text-muted-foreground text-center">{d}</div>)}
              {PERIODS.map((p) => (
                <React.Fragment key={p}>
                  <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground pt-3">P{p}</div>
                  {DAYS.map((d) => {
                    const s = grid[`${d}-${p}`];
                    return (
                      <button key={`${d}-${p}`}
                        onClick={() => openSlot(d, p)}
                        className={cn("p-3 rounded-xl min-h-[68px] text-left transition-colors lift",
                          s ? "bg-primary/10 hover:bg-primary/15" : "bg-muted/40 hover:bg-muted border border-dashed border-border")}
                        data-testid={`admin-slot-${d}-${p}`}>
                        {s ? (
                          <>
                            <div className="text-sm font-medium truncate">{s.subject}</div>
                            <div className="text-[0.65rem] text-muted-foreground mt-1">P{p}</div>
                          </>
                        ) : (
                          <div className="text-xs text-muted-foreground">click to add</div>
                        )}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.day} · Period {editing?.period}</DialogTitle>
            <DialogDescription>Choose the subject and teacher for this slot.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-xl mt-1.5" placeholder="Mathematics" data-testid="admin-slot-subject" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Teacher</Label>
              <Select value={teacherId || "__none"} onValueChange={(v) => setTeacherId(v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-xl mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— none —</SelectItem>
                  {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={clearSlot} className="rounded-xl text-red-500">Clear slot</Button>
            <Button onClick={saveSlot} disabled={!subject.trim()} className="rounded-xl" data-testid="admin-slot-save">Save slot</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fees Batch tab
// ---------------------------------------------------------------------------
function FeesBatchTab({ classes, studentsByClass }) {
  const [form, setForm] = useState({ class_id: "", term: "1", amount: "", description: "Tuition", due_date: "" });
  const [busy, setBusy] = useState(false);

  const count = studentsByClass[form.class_id] || 0;

  const submit = async () => {
    if (!form.class_id || !form.amount || !form.due_date) return toast.error("Fill all fields");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/fees/bulk", {
        class_id: form.class_id,
        term: parseInt(form.term, 10),
        amount: parseFloat(form.amount),
        description: form.description,
        due_date: form.due_date,
      });
      toast.success(`Generated ${data.count} invoice(s)`);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-fees-batch">
      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="text-base">Batch invoice generator</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Class</Label>
            <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="admin-batch-class"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({studentsByClass[c.id] || 0} learners)</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Term</Label>
            <Select value={form.term} onValueChange={(v) => setForm({ ...form, term: v })}>
              <SelectTrigger className="rounded-xl mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{TERMS.map((t) => <SelectItem key={t} value={String(t)}>Term {t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount per learner (R)</Label>
            <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-xl mt-1.5" data-testid="admin-batch-amount" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Due date</Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-xl mt-1.5" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1.5" />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-primary/25 bg-primary/5">
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            {form.class_id && form.amount
              ? <>You are about to create <b>{count}</b> invoice(s) of <b>{zar(form.amount)}</b> each = <b>{zar(count * parseFloat(form.amount || 0))}</b> total.</>
              : "Select a class and amount to preview the batch."}
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={!form.class_id || !form.amount || !form.due_date || busy} className="rounded-xl" data-testid="admin-batch-generate">
                Generate invoices
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm batch invoicing</AlertDialogTitle>
                <AlertDialogDescription>
                  This will create <b>{count}</b> pending invoice(s) — one per learner in the selected class. This cannot be undone in bulk.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={submit} className="rounded-xl">Confirm</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Submissions grading tab
// ---------------------------------------------------------------------------
function SubmissionsTab({ classes }) {
  const [classId, setClassId] = useState("");
  const [hwList, setHwList] = useState([]);
  const [hwId, setHwId] = useState("");
  const [subs, setSubs] = useState([]);
  const [studentMap, setStudentMap] = useState({});

  useEffect(() => {
    if (!classId) { setHwList([]); return; }
    api.get("/homework", { params: { class_id: classId } }).then((r) => {
      setHwList(r.data);
      if (r.data[0]) setHwId(r.data[0].id); else setHwId("");
    });
    api.get("/users", { params: { role: "student", class_id: classId } }).then((r) =>
      setStudentMap(Object.fromEntries(r.data.map((s) => [s.id, s.name])))
    );
  }, [classId]);

  const load = async () => {
    if (!hwId) { setSubs([]); return; }
    const { data } = await api.get(`/homework/${hwId}/submissions`);
    setSubs(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hwId]);

  const setLocal = (id, patch) => setSubs((prev) => prev.map((s) => s.id === id ? { ...s, ...patch } : s));

  const gradeOne = async (s) => {
    try {
      await api.post("/homework/grade", { submission_id: s.id, grade: s.grade || "", feedback: s.feedback || "" });
      toast.success(`Saved grade for ${studentMap[s.student_id] || "learner"}`);
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-submissions">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Class</Label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="rounded-xl mt-1.5 w-48" data-testid="admin-sub-class"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Homework</Label>
          <Select value={hwId} onValueChange={setHwId} disabled={!classId || hwList.length === 0}>
            <SelectTrigger className="rounded-xl mt-1.5 w-72" data-testid="admin-sub-hw"><SelectValue placeholder={classId ? (hwList.length ? "Select homework" : "No homework") : "Pick class"} /></SelectTrigger>
            <SelectContent>{hwList.map((h) => <SelectItem key={h.id} value={h.id}>{h.subject}: {h.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="p-0">
          <div className="divide-y divide-border/60">
            {!hwId && <div className="p-8 text-center text-sm text-muted-foreground">Pick a homework to see submissions.</div>}
            {hwId && subs.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No submissions yet.</div>}
            {subs.map((s) => (
              <div key={s.id} className="p-4 space-y-2" data-testid={`admin-sub-row-${s.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-medium">{studentMap[s.student_id] || "Unknown learner"}</div>
                  <Badge variant="outline" className="rounded-full text-[0.65rem]">Submitted {String(s.submitted_at || "").slice(0, 10)}</Badge>
                </div>
                {s.content && (
                  <div className="p-3 rounded-xl bg-muted/40 text-sm whitespace-pre-wrap">{s.content}</div>
                )}
                <div className="grid grid-cols-[120px,1fr,auto] gap-2 items-center">
                  <Input value={s.grade || ""} onChange={(e) => setLocal(s.id, { grade: e.target.value })}
                         placeholder="Grade" className="rounded-xl h-9" data-testid={`admin-sub-grade-${s.id}`} />
                  <Input value={s.feedback || ""} onChange={(e) => setLocal(s.id, { feedback: e.target.value })}
                         placeholder="Feedback" className="rounded-xl h-9" />
                  <Button size="sm" onClick={() => gradeOne(s)} className="rounded-xl h-9 gap-1" data-testid={`admin-sub-save-${s.id}`}>
                    <Save className="h-3.5 w-3.5" /> Save
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History (audit log) tab
// ---------------------------------------------------------------------------
const ACTION_COLORS = {
  create: "bg-emerald-500/15 text-emerald-500",
  update: "bg-blue-500/15 text-blue-500",
  delete: "bg-red-500/15 text-red-500",
  pay: "bg-primary/15 text-primary",
  grade: "bg-amber-500/15 text-amber-500",
  bulk_marks: "bg-amber-500/15 text-amber-500",
  bulk_fees: "bg-primary/15 text-primary",
  reset_password: "bg-orange-500/15 text-orange-500",
  invite: "bg-violet-500/15 text-violet-500",
  share: "bg-cyan-500/15 text-cyan-500",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function HistoryTab() {
  const [logs, setLogs] = useState([]);
  const [facets, setFacets] = useState({ actions: [], resources: [], actors: [] });
  const [action, setAction] = useState("all");
  const [resource, setResource] = useState("all");
  const [actorId, setActorId] = useState("all");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (action !== "all") params.action = action;
      if (resource !== "all") params.resource = resource;
      if (actorId !== "all") params.actor_id = actorId;
      const { data } = await api.get("/admin/audit-logs", { params });
      setLogs(data);
    } catch (e) { toast.error(errText(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/admin/audit-logs/facets").then((r) => setFacets(r.data));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action, resource, actorId]);

  return (
    <div className="space-y-4" data-testid="admin-history">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Action</Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="rounded-xl mt-1.5 w-40" data-testid="history-filter-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {facets.actions.map((a) => <SelectItem key={a} value={a} className="capitalize">{a.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Resource</Label>
          <Select value={resource} onValueChange={setResource}>
            <SelectTrigger className="rounded-xl mt-1.5 w-40" data-testid="history-filter-resource"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All resources</SelectItem>
              {facets.resources.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Actor</Label>
          <Select value={actorId} onValueChange={setActorId}>
            <SelectTrigger className="rounded-xl mt-1.5 w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Anyone</SelectItem>
              {facets.actors.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name || a.email} ({a.role})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button onClick={load} variant="outline" className="rounded-xl gap-1 h-11" data-testid="history-refresh">
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} /> Refresh
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Audit trail</CardTitle>
          <Badge variant="outline" className="rounded-full">{logs.length} events</Badge>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border/60">
            {logs.length === 0 && !loading && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No events match. Adjust filters or make a change in another tab to see it here.
              </div>
            )}
            {logs.map((l) => {
              const isOpen = expanded[l.id];
              const hasDetails = l.details && Object.keys(l.details).length > 0;
              return (
                <div key={l.id} className="px-4 py-3 hover:bg-muted/30" data-testid={`history-row-${l.id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Badge className={cn("rounded-full capitalize text-[0.65rem]", ACTION_COLORS[l.action] || "bg-muted text-foreground hover:bg-muted")}>
                      {l.action.replace("_", " ")}
                    </Badge>
                    <span className="text-sm">
                      <span className="capitalize font-medium">{l.resource}</span>
                      {l.resource_id ? <span className="text-muted-foreground"> · {String(l.resource_id).slice(-8)}</span> : null}
                    </span>
                    <div className="flex-1" />
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <UserIcon className="h-3 w-3" />
                      <span className="truncate">{l.actor_name || l.actor_email}</span>
                      <Badge variant="outline" className="rounded-full capitalize text-[0.6rem]">{l.actor_role}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground tabular-nums" title={l.timestamp}>{timeAgo(l.timestamp)}</div>
                    {hasDetails && (
                      <Button size="sm" variant="ghost" onClick={() => setExpanded((e) => ({ ...e, [l.id]: !isOpen }))}
                              className="rounded-xl h-7 px-2 text-xs">
                        {isOpen ? "Hide" : "Details"}
                      </Button>
                    )}
                  </div>
                  {isOpen && hasDetails && (
                    <pre className="mt-2 p-3 rounded-lg bg-muted/40 text-[0.7rem] overflow-x-auto">
                      {JSON.stringify(l.details, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root Admin Console
// ---------------------------------------------------------------------------
export default function AdminConsole() {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";
  const [tab, setTab] = useState(isTeacher ? "marks" : "overview");
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [studentsByClass, setStudentsByClass] = useState({});

  // Re-sync default tab once role hydrates (auth may be null on first render).
  useEffect(() => {
    if (isTeacher && (tab === "overview" || tab === "people" || tab === "classes" ||
                       tab === "timetable" || tab === "fees" || tab === "history")) {
      setTab("marks");
    }
  }, [isTeacher, tab]);

  const refreshShared = async () => {
    const [c, t, s] = await Promise.all([
      api.get("/classes"),
      api.get("/users", { params: { role: "teacher" } }),
      api.get("/users", { params: { role: "student" } }),
    ]);
    setClasses(c.data);
    setTeachers(t.data);
    const map = {};
    s.data.forEach((st) => { if (st.class_id) map[st.class_id] = (map[st.class_id] || 0) + 1; });
    setStudentsByClass(map);
  };
  useEffect(() => { refreshShared(); }, []);

  if (user && !["admin", "principal", "teacher"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  const allTabs = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "people", label: "People", icon: Users },
    { key: "classes", label: "Classes", icon: School },
    { key: "marks", label: "Marks entry", icon: ClipboardList },
    { key: "timetable", label: "Timetable", icon: CalendarDays },
    { key: "fees", label: "Fees batch", icon: Wallet },
    { key: "submissions", label: "Submissions", icon: GraduationCap },
    { key: "history", label: "History", icon: History },
  ];

  // Teachers get a scoped console — only what they need to do their job.
  const TEACHER_TABS = new Set(["marks", "submissions"]);
  const tabs = isTeacher ? allTabs.filter((t) => TEACHER_TABS.has(t.key)) : allTabs;

  return (
    <div className="space-y-6" data-testid="admin-console">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">
            {isTeacher ? "Teacher tools" : "Restricted area"}
          </div>
          <h1 className="text-4xl font-bold tracking-tight flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            {isTeacher ? "Teacher Console" : "Admin Console"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1 font-serif-display">
            {isTeacher
              ? "Enter marks and grade submissions for your classes — the rest of the school is managed by admin."
              : "Every input on this page changes what your dashboards, screens and stats show."}
          </p>
        </div>
        <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/15 border-primary/20">
          <Check className="h-3 w-3 mr-1" /> {user?.role} access
        </Badge>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-2xl h-auto p-1 flex flex-wrap w-full">
          {tabs.map((t) => (
            <TabsTrigger key={t.key} value={t.key} className="rounded-xl py-2 px-4 gap-2 data-[state=active]:shadow-sm" data-testid={`admin-tab-${t.key}`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {!isTeacher && <TabsContent value="overview" className="mt-6"><OverviewTab setTab={setTab} /></TabsContent>}
        {!isTeacher && <TabsContent value="people" className="mt-6"><PeopleTab classes={classes} /></TabsContent>}
        {!isTeacher && <TabsContent value="classes" className="mt-6"><ClassesTab classes={classes} refresh={refreshShared} teachers={teachers} studentsByClass={studentsByClass} /></TabsContent>}
        <TabsContent value="marks" className="mt-6"><MarksTab classes={classes} /></TabsContent>
        {!isTeacher && <TabsContent value="timetable" className="mt-6"><TimetableTab classes={classes} teachers={teachers} /></TabsContent>}
        {!isTeacher && <TabsContent value="fees" className="mt-6"><FeesBatchTab classes={classes} studentsByClass={studentsByClass} /></TabsContent>}
        <TabsContent value="submissions" className="mt-6"><SubmissionsTab classes={classes} /></TabsContent>
        {!isTeacher && <TabsContent value="history" className="mt-6"><HistoryTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
